import React from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  ToggleButton,
  Typography,
} from '@mui/material';

/**
 * Pilot-only slot fields for summary / orders (no Prana fields).
 * @param {Record<string, unknown>} slot
 */
export function slotToSummaryPayload(slot) {
  if (!slot || typeof slot !== 'object') return null;
  return {
    id: slot.id,
    displayTime: slot.displayTime,
    startTime: slot.startTime,
    endTime: slot.endTime,
    active: slot.active !== false,
    availability: slot.availability ?? null,
  };
}

export default function CheckoutSlots({
  storeId,
  deliveryDay,
  onDeliveryDayChange,
  slots,
  slotsLoading,
  slotsError,
  selectedSlotId,
  onSelectSlotId,
}) {
  const allSlotsDisabled =
    slots.length > 0 &&
    slots.every((s) => Boolean(s.isDisabled) || s.active === false);

  if (!storeId) {
    return (
      <Alert severity="info" sx={{ mb: 0 }}>
        Select a delivery address to see available time slots.
      </Alert>
    );
  }

  const dayToggleSx = {
    flex: 1,
    py: 1.5,
    px: 2,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 2,
    typography: 'body2',
  };

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
        Delivery slot
      </Typography>

      <Stack direction="row" spacing={2} sx={{ mb: 2.5, width: '100%' }}>
        <ToggleButton
          value="today"
          selected={deliveryDay === 'today'}
          onClick={() => onDeliveryDayChange('today')}
          sx={dayToggleSx}
        >
          Today
        </ToggleButton>
        <ToggleButton
          value="tomorrow"
          selected={deliveryDay === 'tomorrow'}
          onClick={() => onDeliveryDayChange('tomorrow')}
          sx={dayToggleSx}
        >
          Tomorrow
        </ToggleButton>
      </Stack>

      {slotsError ? (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {slotsError}
        </Alert>
      ) : null}

      {slotsLoading ? (
        <Box display="flex" justifyContent="center" py={2}>
          <CircularProgress size={28} />
        </Box>
      ) : slots.length === 0 || allSlotsDisabled ? (
        <Typography variant="body2" color="text.secondary">
          No slots available for this day.
        </Typography>
      ) : (
        <>

          <Box
            role="listbox"
            aria-label="Delivery time slots"
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 1,
              alignItems: 'stretch',
            }}
          >
            {slots.map((slot) => {
            const id = String(slot.id);
            const disabled = Boolean(slot.isDisabled) || slot.active === false;
            const selected = String(selectedSlotId || '') === id;
            const label = slot.displayTime || id;
            const g = (theme) => theme.spacing(1);
            return (
              <Chip
                key={id}
                role="option"
                aria-selected={selected}
                size="small"
                label={label}
                clickable={!disabled}
                disabled={disabled}
                color={selected && !disabled ? 'primary' : 'default'}
                variant={selected && !disabled ? 'filled' : 'outlined'}
                onClick={() => {
                  if (!disabled) onSelectSlotId(id);
                }}
                sx={(theme) => ({
                  flexGrow: 0,
                  flexShrink: 1,
                  flexBasis: `calc((100% - 2 * ${g(theme)}) / 3)`,
                  maxWidth: `calc((100% - 2 * ${g(theme)}) / 3)`,
                  minWidth: 0,
                  height: 'auto',
                  minHeight: 30,
                  alignSelf: 'stretch',
                  borderRadius: 2,
                  '& .MuiChip-label': {
                    px: 0.5,
                    py: 0.5,
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    lineHeight: 1.15,
                    whiteSpace: 'normal',
                    textAlign: 'center',
                    display: 'block',
                  },
                  ...(disabled
                    ? {
                        opacity: 0.55,
                        cursor: 'not-allowed',
                      }
                    : {}),
                })}
              />
            );
            })}
          </Box>
        </>
      )}
    </Box>
  );
}
